import { StripeMetaService } from './stripe-meta.service';
import { NavigationExtras, Router } from '@angular/router';
import { DebugModeService } from '../../general/debug-mode.service';
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { StripeSetupService } from './stripe-setup.service';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { TranslateService } from '@ngx-translate/core';
import { StripeInstance } from 'ngx-stripe';
import { ProductAccountPageParam, Price } from 'src/app/interfaces/product';
import { Item } from 'src/app/interfaces/transaction';
import { Voucher } from 'src/app/interfaces/voucher';
import { ErrorService } from '../../general/error.service';
import { PopupService } from '../../general/popup.service';
import { UpdateByService } from '../../user/update-by.service';
import { TransactionType } from 'src/app/types/transaction';
import { CheckoutParam } from 'src/app/interfaces/stripe';
import { GAItem, GAPurchase } from 'src/app/interfaces/analytics';
import { AnalyticsService } from '../../general/analytics.service';
import { BackService } from '../../general/back.service';
import { Platform } from '@ionic/angular';
import { LinkService } from '../../general/link.service';

@Injectable({
  providedIn: 'root'
})
export class StripeCheckoutService implements OnInit, OnDestroy {

  constructor(
    private platform: Platform,
    private router: Router,
    private fns: AngularFireFunctions,
    private translate: TranslateService,
    private stripeSetupService: StripeSetupService,
    private stripeMetaService: StripeMetaService,
    private updateByService: UpdateByService,
    private analyticsService: AnalyticsService,
    private backService: BackService,
    private debugModeService: DebugModeService,
    private linkService: LinkService,
    private popupService: PopupService,
    private errorService: ErrorService,
  ) { }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
  }

  async initialStripeCheckoutSessionCall(
    transactionType: TransactionType,
    stripe: StripeInstance,
    payment: Price,
    itemList: Item[],
    totalQty: number,
    gift: boolean,
    bulk: boolean,
    referralCode: string,
    voucher: Voucher,
    upgrade: boolean,
    trial: boolean,
    accountId: string,
    productAccountPageParam?: ProductAccountPageParam
  ) {
    // await this.goStripeCheckoutPage();
    const debugMode = this.debugModeService?.debugMode;
    const stripeKey = this.stripeSetupService.getStripeKey(payment?.currency);
    const customer = this.stripeSetupService.getCustomerData();
    const createBy = this.updateByService.generateUpdateBy();
    const checkoutParam = this.stripeMetaService.setupCheckoutParam(
      transactionType,
      createBy.uid,
      createBy.uuid,
      stripeKey,
      customer.name,
      itemList,
      totalQty,
      debugMode,
      upgrade,
      trial,
      gift,
      bulk,
      referralCode,
      voucher?.voucherId,
      accountId,
    );
    const param = {
      transactionType,
      customer,
      payment,
      checkoutParam,
      createBy,
    };
    await this.fns.httpsCallable('initialStripeCheckoutSessionCall')(param)
    .toPromise().then(async (response) => {
      if (response?.error) {
        this.errorService.logError(response?.error);
        this.promptPaymentError(transactionType, payment, productAccountPageParam, checkoutParam);
      } else if (response?.session?.id) {
        this.redirectToCheckout(stripe, response.session.id, payment, productAccountPageParam, 'account', checkoutParam);
      } else if (!payment?.amount && response?.transactionId) {
        if (transactionType === 'account' && response?.creditIdList?.length) {
          this.setupTrialAnalytics(trial, accountId);
          this.setupPurchaseAnalytics(payment.currency, payment.amount, response.transactionId, transactionType, voucher?.voucherId, 0, itemList);
          this.goAccountPaymentResultPage(response.creditIdList[0], upgrade, trial, accountId);
        } else if (transactionType === 'blast' && response?.blastCreditIdList?.length) {
          this.goSmsResultPage(response.blastCreditIdList);
        } else {
          this.errorService.logError(response);
          this.promptPaymentError(transactionType, payment, productAccountPageParam, checkoutParam);
        }
      } else {
        this.errorService.logError(response);
        this.promptPaymentError(transactionType, payment, productAccountPageParam, checkoutParam);
      }
    }).catch((err: any) => {
      console.log(err);
      this.errorService.logError(err);
      this.promptPaymentError(transactionType, payment, productAccountPageParam, checkoutParam);
      return null;
    });
  }

  async redirectToCheckout(stripe: StripeInstance, sessionId: string, total?: Price, productAccountPageParam?: ProductAccountPageParam, 
                           transactionType?: TransactionType, checkoutParam?: CheckoutParam) {
    if (stripe && sessionId) {
      const checkout = stripe.redirectToCheckout({ sessionId }).subscribe(async (result: any) => {
        if (result) {
          checkout.unsubscribe();
          // await this.functionService.delay(this.functionService.randomNumber(50, 300));
          if (result.error) {
            this.errorService.logError('redirect error');
            this.errorService.logError(result);
            this.promptPaymentError(transactionType, total, productAccountPageParam, checkoutParam);
          } else if (this.platform.is('hybrid')) {
            setTimeout(() => {
              this.linkService.goMainPage();
            }, 500);
            // this.goStripeStatusPage(sessionId, total?.currency, transactionType, platformType, uid, productAccountPageParam);
          }
        }
      });
    } else {
      this.promptPaymentError(transactionType, total, productAccountPageParam, checkoutParam);
    }
  }

  async promptPaymentError(transactionType: TransactionType, total?: Price, productAccountPageParam?: ProductAccountPageParam, checkoutParam?: CheckoutParam) {
    const alert = await this.popupService.presentAlert(this.translate.instant('STRIPE.checkout.msg.error'));
    alert.onDidDismiss().then(() => {
      this.goBack(transactionType, total, productAccountPageParam, checkoutParam);
    });
  }

  setupTrialAnalytics(trial: boolean, accountId: string) {
    if (trial && accountId) {
      this.analyticsService.upgradeTrial(accountId);
    }
  }

  setupPurchaseAnalytics(currency: string, value: number, transactionId: string, transactionType: TransactionType, coupon: string, tax: number, itemList: Item[]) {
    const items: GAItem[] = [];
    itemList?.forEach((item: Item) => {
      if (item?.productId) {
        items.push({
          item_id: item.productId,
          item_category: transactionType,
          item_name: item?.productName,
          quantity: item?.qty,
          price: item?.payment?.amount,
          currency: item?.payment?.currency,
        });
      }
    });
    const gaPurchase: GAPurchase = {
      currency,
      value,
      transaction_id: transactionId,
      coupon,
      tax,
      items
    };
    this.analyticsService.purchase(gaPurchase);
  }


  goBack(transactionType: TransactionType, total?: Price, productAccountPageParam?: ProductAccountPageParam, checkoutParam?: CheckoutParam) {
    if (transactionType === 'account' && checkoutParam?.stripeKey) {
      this.goAccountPage(total, productAccountPageParam, checkoutParam);
    } else if (transactionType === 'blast') {
      this.goSmsPackagePage();
    } else {
      this.backService.goBack();
    }
  }

  goAccountPage(total: Price, productAccountPageParam?: ProductAccountPageParam, checkoutParam?: CheckoutParam) {
    const navigationExtras: NavigationExtras = {
      state: {
        total,
        accountId: checkoutParam?.accountId,
        currency: total?.currency,
        itemList: checkoutParam?.itemList,
        referralCode: checkoutParam?.referralCode,
        voucherId: checkoutParam?.voucherId,
        giftMode: checkoutParam?.gift ? true : false,
        bulkMode: checkoutParam?.bulk ? true : false,
        upgradeTrial: checkoutParam?.trial ? true : false,
        upgradeMode: checkoutParam?.upgrade ? true : false,
        disableUpgrade: productAccountPageParam?.disableUpgrade ? true : false,
        disableCreditPrompt: productAccountPageParam?.disableCreditPrompt ? true : false,
        backPremiumPage: productAccountPageParam?.backPremiumPage ? true : false,
        backAddPage: productAccountPageParam?.backAddPage ? true : false,
      }
    };
    this.router.navigate(['/subscription/account'], navigationExtras);
  }

  goSmsPackagePage() {
    const navigationExtras: NavigationExtras = {
      state: {
      }
    };
    this.router.navigate(['/blast/package'], navigationExtras);
  }

  goAccountPaymentResultPage(creditId?: string, upgradeMode?: boolean, trialMode?: boolean, accountId?: string) {
    const navigationExtras: NavigationExtras = {
      state: {
        result: 'success',
        creditId,
        upgradeMode,
        trialMode,
        accountId
      }
    };
    this.router.navigate(['/subscription/payment-result'], navigationExtras);
  }

  goSmsResultPage(blastCreditIdList: string[]) {
    if (blastCreditIdList?.length) {
      const navigationExtras: NavigationExtras = {
        state: {
          blastCreditIdList
        }
      };
      this.router.navigate(['/blast/result'], navigationExtras);
    }
  }
}
